import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/button';
import {
  ChevronDown,
  ChevronUp,
  Mic,
  Upload,
  Radio,
  X,
  Maximize2,
} from 'lucide-react';
import LiveRecordingMonitor from './LiveRecordingMonitor';

interface Recording {
  id: string;
  title: string;
  status: string;
  venueId?: string;
  venueName?: string;
  venue_name?: string;
  startTime?: string;
  start_time?: string;
  captureMinutes?: number;
  capture_minutes?: number;
  metadata?: any;
}

interface FloatingRecordingWidgetProps {
  recordings: Recording[];
  isWebSocketConnected?: boolean;
  onDismiss?: () => void;
}

const FloatingRecordingWidget: React.FC<FloatingRecordingWidgetProps> = ({
  recordings,
  isWebSocketConnected = true,
  onDismiss,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isDetailView, setIsDetailView] = useState(false);

  if (recordings.length === 0) {
    return null;
  }

  const activeCount = recordings.filter(r => r.status === 'recording').length;
  const uploadingCount = recordings.filter(r => r.status === 'uploading').length;

  return (
    <>
      {/* Floating widget */}
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className="fixed bottom-4 right-4 z-50 w-96 max-w-[calc(100vw-2rem)]"
      >
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl border-2 border-red-500/20 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-red-500 to-orange-500 p-3 cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-white">
                <motion.div
                  animate={{
                    scale: [1, 1.2, 1],
                    opacity: [1, 0.6, 1],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                  }}
                >
                  <Radio className="h-5 w-5" />
                </motion.div>
                <div>
                  <div className="font-semibold text-sm">
                    {activeCount > 0 && `${activeCount} Recording${activeCount !== 1 ? 's' : ''}`}
                    {activeCount > 0 && uploadingCount > 0 && ' • '}
                    {uploadingCount > 0 && `${uploadingCount} Uploading`}
                  </div>
                  <div className="text-xs opacity-90">
                    Live monitoring
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-white hover:bg-white/20"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsDetailView(true);
                  }}
                >
                  <Maximize2 className="h-4 w-4" />
                </Button>
                {onDismiss && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-white hover:bg-white/20"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDismiss();
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-white hover:bg-white/20"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsExpanded(!isExpanded);
                  }}
                >
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronUp className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>

          {/* Content */}
          <AnimatePresence>
            {isExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="p-3 max-h-96 overflow-y-auto">
                  {/* Compact list view */}
                  <div className="space-y-2">
                    {recordings.map((recording) => {
                      const getStatusIcon = () => {
                        switch (recording.status) {
                          case 'recording':
                            return <Mic className="h-4 w-4 text-red-600 animate-pulse" />;
                          case 'uploading':
                            return <Upload className="h-4 w-4 text-orange-600" />;
                          default:
                            return <Radio className="h-4 w-4 text-gray-600" />;
                        }
                      };

                      return (
                        <div
                          key={recording.id}
                          className="p-2 rounded-lg bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer"
                          onClick={() => setIsDetailView(true)}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              {getStatusIcon()}
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-sm truncate">
                                  {recording.title}
                                </div>
                                <div className="text-xs text-gray-600 dark:text-gray-400 truncate">
                                  {recording.venueName || recording.venue_name || 'Unknown Venue'}
                                </div>
                              </div>
                            </div>
                            <div className="text-xs text-gray-500 whitespace-nowrap">
                              {recording.startTime || recording.start_time
                                ? new Date(recording.startTime || recording.start_time || '').toLocaleTimeString()
                                : 'N/A'}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Expand button */}
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full mt-3"
                    onClick={() => setIsDetailView(true)}
                  >
                    <Maximize2 className="h-3.5 w-3.5 mr-2" />
                    View Details
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>

      {/* Detailed view modal */}
      <AnimatePresence>
        {isDetailView && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setIsDetailView(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-gradient-to-r from-red-500 to-orange-500 p-4 flex items-center justify-between z-10">
                <div className="flex items-center gap-3 text-white">
                  <Radio className="h-6 w-6 animate-pulse" />
                  <div>
                    <h2 className="text-xl font-bold">Live Recording Monitor</h2>
                    <p className="text-sm opacity-90">
                      {activeCount > 0 && `${activeCount} active`}
                      {activeCount > 0 && uploadingCount > 0 && ' • '}
                      {uploadingCount > 0 && `${uploadingCount} uploading`}
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-white hover:bg-white/20"
                  onClick={() => setIsDetailView(false)}
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>

              <div className="p-6">
                <LiveRecordingMonitor
                  recordings={recordings}
                  isWebSocketConnected={isWebSocketConnected}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default FloatingRecordingWidget;
